/* -----------------------------------------------------------------------
 * Nonogram Creator — Canvas Editor
 *
 * Multi-layer canvas for the puzzle creator. Two modes:
 *
 * DRAW MODE:
 *   - Image overlay, crosshair, ambiguous cell highlights
 *   - Tool previews (brush, spray)
 *   - Full-grid clue numbers
 *
 * MULTI PUZZLE MODE:
 *   - Per-region clue numbers (colored per region)
 *   - Resize handles at each region corner
 *   - Delete button in each region (when more than one)
 *   - Drag to create new regions
 *
 * Both modes render: background, grid cells, region boundaries, grid lines.
 * ----------------------------------------------------------------------- */

import { useRef, useEffect, useCallback } from 'react';
import { cluesFromGrid, sliceGrid } from '@org/nonogram-engine';

/* ---------- colors ---------- */

const COL_BG = '#FFF5E1';
const COL_FILLED = '#f0b830';
const COL_GRID = '#c8b898';
const COL_SEP = '#a89060';
const COL_CLUE = '#5a3a1a';
const COL_CROSSHAIR = 'rgba(210, 190, 140, 0.3)';
const COL_AMBIGUOUS = '#ff8a00';
const COL_BRUSH_PREVIEW = 'rgba(240, 184, 48, 0.32)';
const COL_SPRAY_PREVIEW = 'rgba(218, 162, 72, 0.24)';
const COL_IMPORT_PREVIEW = 'rgba(72, 145, 216, 0.58)';

const GOLDEN_ANGLE = 137.508;

function getRegionPalette(index: number) {
  const hue = (24 + index * GOLDEN_ANGLE) % 360;
  const saturation = 34 + (index % 3) * 4;
  const fillLightness = 70 + (index % 2) * 3;
  const borderLightness = 52 + (index % 2) * 4;
  const clueLightness = 34 + (index % 2) * 3;

  return {
    fill: `hsla(${hue.toFixed(1)}, ${saturation}%, ${fillLightness}%, 0.09)`,
    border: `hsla(${hue.toFixed(1)}, ${saturation}%, ${borderLightness}%, 0.58)`,
    clue: `hsl(${hue.toFixed(1)}, ${Math.min(48, saturation + 6)}%, ${clueLightness}%)`,
  };
}

/* ---------- types ---------- */

interface Region {
  row: number;
  col: number;
  width: number;
  height: number;
}

type Corner = 'tl' | 'tr' | 'bl' | 'br';

interface OverlayPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface OverlayLayer {
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap;
  placement: OverlayPlacement;
}

export interface RegionBounds {
  row: number;
  col: number;
  width: number;
  height: number;
}

export interface CreatorCanvasProps {
  grid: boolean[][];
  rows: number;
  cols: number;
  mode: 'draw' | 'regions' | 'size' | 'image';
  /* Clue display */
  showClues: boolean;
  showSpanHints: boolean;
  /* Draw mode */
  overlayLayers: OverlayLayer[];
  pendingOverlayImage: HTMLImageElement | HTMLCanvasElement | ImageBitmap | null;
  pendingOverlayPlacement: OverlayPlacement | null;
  overlayOpacity: number;
  overlayVisible: boolean;
  overlayScale: number;
  overlayOffsetX: number;
  overlayOffsetY: number;
  overlayInteractive: boolean;
  ambiguousCells: Set<string>;
  uniquenessHint: { row: number; col: number; shouldFill: boolean } | null;
  pendingImportGrid: boolean[][] | null;
  previewCells: Set<string>;
  previewMode: 'brush' | 'spray' | 'none';
  onCellDown: (row: number, col: number) => void;
  onCellMove: (row: number, col: number) => void;
  onCellUp: () => void;
  onCellLeave: () => void;
  onOverlayOffsetChange: (offsetX: number, offsetY: number) => void;
  onOverlayScaleChange: (scale: number) => void;
  /* Regions */
  regions: Region[];
  regionDifficulties?: number[];
  regionUniqueness?: (boolean | null)[];
  regionUniquenessHints?: ({ row: number; col: number; shouldFill: boolean } | null)[];
  selectedRegion: number | null;
  onRegionCreate: (bounds: RegionBounds) => void;
  onRegionResize: (index: number, bounds: RegionBounds) => void;
  onRegionDelete: (index: number) => void;
  onRegionSelect: (index: number | null) => void;
}

/* ---------- drawing helpers ---------- */

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawOverlayImage(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap | null,
  placement: OverlayPlacement | null,
  gameLeft: number,
  gameTop: number,
  cw: number,
  ch: number,
  opacity: number,
) {
  if (!image || !placement) return;
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.drawImage(
    image,
    gameLeft + placement.x * cw,
    gameTop + placement.y * ch,
    placement.width * cw,
    placement.height * ch,
  );
  ctx.restore();
}

/* ---------- component ---------- */

export function CreatorCanvas({
  grid, rows, cols, mode,
  showClues, showSpanHints,
  overlayLayers, pendingOverlayImage, pendingOverlayPlacement,
  overlayOpacity, overlayVisible, overlayScale, overlayOffsetX, overlayOffsetY,
  overlayInteractive,
  ambiguousCells, uniquenessHint, pendingImportGrid, previewCells, previewMode,
  onCellDown, onCellMove, onCellUp, onCellLeave,
  onOverlayOffsetChange, onOverlayScaleChange,
  regions, regionDifficulties, regionUniqueness, regionUniquenessHints, selectedRegion,
  onRegionCreate, onRegionResize, onRegionDelete, onRegionSelect,
}: CreatorCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hoverRef = useRef<{ row: number; col: number }>({ row: -1, col: -1 });
  const rafRef = useRef(0);

  /* --- Full-grid clues (always computed — used for margin sizing) --- */

  const cluesRef = useRef({ rows: [] as number[][], cols: [] as number[][] });
  try {
    const c = cluesFromGrid(grid);
    cluesRef.current = {
      rows: c.rows.map((r) => (r.length === 0 ? [0] : r)),
      cols: c.cols.map((co) => (co.length === 0 ? [0] : co)),
    };
  } catch {
    cluesRef.current = {
      rows: Array.from({ length: rows }, () => [0]),
      cols: Array.from({ length: cols }, () => [0]),
    };
  }

  /* --- Per-region clues (for multi puzzle mode) --- */

  const regionCluesRef = useRef<{ rows: number[][]; cols: number[][] }[]>([]);
  if (regions.length > 0) {
    regionCluesRef.current = regions.map((region) => {
      try {
        const slice = sliceGrid(grid, region);
        const c = cluesFromGrid(slice);
        return {
          rows: c.rows.map((r) => (r.length === 0 ? [0] : r)),
          cols: c.cols.map((co) => (co.length === 0 ? [0] : co)),
        };
      } catch {
        return {
          rows: Array.from({ length: region.height }, () => [0]),
          cols: Array.from({ length: region.width }, () => [0]),
        };
      }
    });
  } else {
    regionCluesRef.current = [];
  }

  /* --- Props ref for draw loop --- */

  const propsRef = useRef({
    grid, rows, cols, mode,
    showClues, showSpanHints,
    overlayLayers, pendingOverlayImage, pendingOverlayPlacement,
    overlayOpacity, overlayVisible, overlayScale, overlayOffsetX, overlayOffsetY,
    overlayInteractive,
    ambiguousCells, uniquenessHint, pendingImportGrid, regions, regionDifficulties, regionUniqueness, regionUniquenessHints, selectedRegion,
    previewCells, previewMode,
    clues: cluesRef.current,
    regionClues: regionCluesRef.current,
  });
  propsRef.current = {
    grid, rows, cols, mode,
    showClues, showSpanHints,
    overlayLayers, pendingOverlayImage, pendingOverlayPlacement,
    overlayOpacity, overlayVisible, overlayScale, overlayOffsetX, overlayOffsetY,
    overlayInteractive,
    ambiguousCells, uniquenessHint, pendingImportGrid, regions, regionDifficulties, regionUniqueness, regionUniquenessHints, selectedRegion,
    previewCells, previewMode,
    clues: cluesRef.current,
    regionClues: regionCluesRef.current,
  };

  /* --- Layout dimensions --- */

  const rowClues = cluesRef.current.rows;
  const colClues = cluesRef.current.cols;
  const clueRowLen = Math.max(1, ...rowClues.map((c) => c.length));
  const clueColLen = Math.max(1, ...colClues.map((c) => c.length));
  const maxRowClueLen = (showClues ? clueRowLen : 0) + (showSpanHints ? 1 : 0);
  const maxColClueLen = (showClues ? clueColLen : 0) + (showSpanHints ? 1 : 0);
  const totalCols = maxRowClueLen + cols;
  const totalRows = maxColClueLen + rows;

  /* --- Coordinate helpers --- */

  /** Compute clue-area slots (row/col) accounting for showClues + showSpanHints. */
  const getClueSlots = (p: typeof propsRef.current) => {
    const clueMrl = Math.max(1, ...p.clues.rows.map((c) => c.length));
    const clueMcl = Math.max(1, ...p.clues.cols.map((c) => c.length));
    const mrl = (p.showClues ? clueMrl : 0) + (p.showSpanHints ? 1 : 0);
    const mcl = (p.showClues ? clueMcl : 0) + (p.showSpanHints ? 1 : 0);
    return { mrl, mcl, clueMrl, clueMcl };
  };

  const cellFromPixel = useCallback((px: number, py: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = px - rect.left;
    const y = py - rect.top;
    const p = propsRef.current;
    const { mrl, mcl } = getClueSlots(p);
    const cw = rect.width / (mrl + p.cols);
    const ch = rect.height / (mcl + p.rows);
    const col = Math.floor(x / cw) - mrl;
    const row = Math.floor(y / ch) - mcl;
    if (row >= 0 && col >= 0 && row < p.rows && col < p.cols) return { row, col };
    return null;
  }, []);

  /** Snap to nearest grid intersection (0..rows, 0..cols) for region handles. */
  const gridPosFromPixel = useCallback((px: number, py: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = px - rect.left;
    const y = py - rect.top;
    const p = propsRef.current;
    const { mrl, mcl } = getClueSlots(p);
    const cw = rect.width / (mrl + p.cols);
    const ch = rect.height / (mcl + p.rows);
    return {
      row: Math.max(0, Math.min(p.rows, Math.round(y / ch - mcl))),
      col: Math.max(0, Math.min(p.cols, Math.round(x / cw - mrl))),
    };
  }, []);

  /* --- Hit testing for regions mode --- */

  const hitTest = useCallback(
    (clientX: number, clientY: number):
      | { type: 'handle'; regionIndex: number; corner: Corner }
      | { type: 'delete'; regionIndex: number }
      | { type: 'region'; regionIndex: number }
      | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const p = propsRef.current;
      const { mrl, mcl } = getClueSlots(p);
      const cw = rect.width / (mrl + p.cols);
      const ch = rect.height / (mcl + p.rows);
      const gameLeft = mrl * cw;
      const gameTop = mcl * ch;
      const handleRadius = Math.max(8, Math.min(16, Math.min(cw, ch) * 0.45));

      // Check handles (highest priority — selected region first so shared corners resolve to it)
      const checkOrder: number[] = [];
      if (p.selectedRegion != null && p.selectedRegion >= 0 && p.selectedRegion < p.regions.length) {
        checkOrder.push(p.selectedRegion);
      }
      for (let ri = p.regions.length - 1; ri >= 0; ri--) {
        if (ri !== p.selectedRegion) checkOrder.push(ri);
      }
      for (const ri of checkOrder) {
        const region = p.regions[ri];
        const corners: [Corner, number, number][] = [
          ['tl', gameLeft + region.col * cw, gameTop + region.row * ch],
          ['tr', gameLeft + (region.col + region.width) * cw, gameTop + region.row * ch],
          ['bl', gameLeft + region.col * cw, gameTop + (region.row + region.height) * ch],
          ['br', gameLeft + (region.col + region.width) * cw, gameTop + (region.row + region.height) * ch],
        ];
        for (const [corner, cx, cy] of corners) {
          if (Math.sqrt((x - cx) ** 2 + (y - cy) ** 2) <= handleRadius) {
            return { type: 'handle', regionIndex: ri, corner };
          }
        }
      }

      // Check "Remove" pill buttons
      for (let ri = p.regions.length - 1; ri >= 0; ri--) {
        const region = p.regions[ri];
        const fits = region.width >= 4;
        const remCx = fits
          ? gameLeft + (region.col + region.width - 2) * cw
          : gameLeft + (region.col + region.width / 2) * cw;
        const remCy = fits
          ? gameTop + (region.row + 0.5) * ch
          : gameTop + (region.row + region.height / 2) * ch;
        const pw = cw * 2;
        const ph = ch * 0.7;
        if (
          x >= remCx - pw / 2 && x <= remCx + pw / 2 &&
          y >= remCy - ph / 2 && y <= remCy + ph / 2
        ) {
          return { type: 'delete', regionIndex: ri };
        }
      }

      // Check region bodies
      for (let ri = p.regions.length - 1; ri >= 0; ri--) {
        const region = p.regions[ri];
        const rx = gameLeft + region.col * cw;
        const ry = gameTop + region.row * ch;
        if (x >= rx && x <= rx + region.width * cw && y >= ry && y <= ry + region.height * ch) {
          return { type: 'region', regionIndex: ri };
        }
      }

      return null;
    },
    [],
  );

  /* --- Internal drag state for regions mode --- */

  const resizeDragRef = useRef<{
    regionIndex: number;
    corner: Corner;
    fixedCell: { row: number; col: number };
    currentCell: { row: number; col: number };
  } | null>(null);

  const createDragRef = useRef<{
    start: { row: number; col: number };
    end: { row: number; col: number };
  } | null>(null);
  const overlayDragRef = useRef<{
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);

  /** Which delete button is hovered (region index, or -1). */
  const hoverDeleteRef = useRef(-1);

  /* --- Callback refs (avoids stale closures in pointer handlers) --- */

  const callbacksRef = useRef({
    onCellDown, onCellMove, onCellUp, onCellLeave,
    onOverlayOffsetChange, onOverlayScaleChange,
    onRegionCreate, onRegionResize, onRegionDelete, onRegionSelect,
  });
  callbacksRef.current = {
    onCellDown, onCellMove, onCellUp, onCellLeave,
    onOverlayOffsetChange, onOverlayScaleChange,
    onRegionCreate, onRegionResize, onRegionDelete, onRegionSelect,
  };

  /* --- Pointer handlers --- */

  const draggingRef = useRef(false);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as Element).setPointerCapture(e.pointerId);
      draggingRef.current = true;
      const p = propsRef.current;
      const cb = callbacksRef.current;

      if (p.mode === 'size') {
        draggingRef.current = false;
      } else if (p.mode === 'image') {
        if (p.overlayInteractive && p.pendingOverlayImage && p.overlayVisible) {
          overlayDragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startOffsetX: p.overlayOffsetX,
            startOffsetY: p.overlayOffsetY,
          };
          const canvas = canvasRef.current;
          if (canvas) canvas.style.cursor = 'grabbing';
        } else {
          draggingRef.current = false;
        }
      } else if (p.mode === 'regions') {
        const hit = hitTest(e.clientX, e.clientY);
        if (hit?.type === 'handle') {
          const region = p.regions[hit.regionIndex];
          const fixed: Record<Corner, { row: number; col: number }> = {
            tl: { row: region.row + region.height, col: region.col + region.width },
            tr: { row: region.row + region.height, col: region.col },
            bl: { row: region.row, col: region.col + region.width },
            br: { row: region.row, col: region.col },
          };
          const moving: Record<Corner, { row: number; col: number }> = {
            tl: { row: region.row, col: region.col },
            tr: { row: region.row, col: region.col + region.width },
            bl: { row: region.row + region.height, col: region.col },
            br: { row: region.row + region.height, col: region.col + region.width },
          };
          resizeDragRef.current = {
            regionIndex: hit.regionIndex,
            corner: hit.corner,
            fixedCell: fixed[hit.corner],
            currentCell: moving[hit.corner],
          };
          cb.onRegionSelect(hit.regionIndex);
        } else if (hit?.type === 'delete') {
          cb.onRegionDelete(hit.regionIndex);
          draggingRef.current = false;
        } else if (hit?.type === 'region') {
          cb.onRegionSelect(hit.regionIndex);
          draggingRef.current = false;
        } else {
          const cell = cellFromPixel(e.clientX, e.clientY);
          if (cell) {
            createDragRef.current = { start: cell, end: cell };
          }
          cb.onRegionSelect(null);
        }
      } else {
        const cell = cellFromPixel(e.clientX, e.clientY);
        if (cell) {
          hoverRef.current = cell;
          cb.onCellDown(cell.row, cell.col);
        }
      }
    },
    [cellFromPixel, hitTest],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const p = propsRef.current;
      const cb = callbacksRef.current;

      if (p.mode === 'size') {
        const canvas = canvasRef.current;
        if (canvas) canvas.style.cursor = 'default';
      } else if (p.mode === 'image') {
        const canvas = canvasRef.current;
        if (overlayDragRef.current && draggingRef.current && canvas) {
          const rect = canvas.getBoundingClientRect();
          const { mrl, mcl } = getClueSlots(p);
          const boardWidth = p.cols * (rect.width / (mrl + p.cols));
          const boardHeight = p.rows * (rect.height / (mcl + p.rows));
          const deltaX = (e.clientX - overlayDragRef.current.startX) / Math.max(1, boardWidth);
          const deltaY = (e.clientY - overlayDragRef.current.startY) / Math.max(1, boardHeight);
          cb.onOverlayOffsetChange(
            overlayDragRef.current.startOffsetX + deltaX,
            overlayDragRef.current.startOffsetY + deltaY,
          );
          canvas.style.cursor = 'grabbing';
        } else if (canvas) {
          canvas.style.cursor =
            p.overlayInteractive && p.pendingOverlayImage && p.overlayVisible ? 'grab' : 'default';
        }
      } else if (p.mode === 'regions') {
        if (resizeDragRef.current) {
          const pos = gridPosFromPixel(e.clientX, e.clientY);
          if (pos) resizeDragRef.current = { ...resizeDragRef.current, currentCell: pos };
        } else if (createDragRef.current && draggingRef.current) {
          const cell = cellFromPixel(e.clientX, e.clientY);
          if (cell) createDragRef.current = { ...createDragRef.current, end: cell };
        } else if (!draggingRef.current) {
          // Cursor hint + hover tracking
          const hit = hitTest(e.clientX, e.clientY);
          hoverDeleteRef.current = hit?.type === 'delete' ? hit.regionIndex : -1;
          const canvas = canvasRef.current;
          if (canvas) {
            if (hit?.type === 'handle') {
              const cursors: Record<Corner, string> = {
                tl: 'nwse-resize', tr: 'nesw-resize',
                bl: 'nesw-resize', br: 'nwse-resize',
              };
              canvas.style.cursor = cursors[hit.corner];
            } else if (hit?.type === 'delete') {
              canvas.style.cursor = 'pointer';
            } else if (hit?.type === 'region') {
              canvas.style.cursor = 'pointer';
            } else {
              canvas.style.cursor = 'crosshair';
            }
          }
        }
      } else {
        const cell = cellFromPixel(e.clientX, e.clientY);
        if (cell) {
          const changed = cell.row !== hoverRef.current.row || cell.col !== hoverRef.current.col;
          hoverRef.current = cell;
          if (changed || draggingRef.current) {
            cb.onCellMove(cell.row, cell.col);
          }
        } else {
          if (hoverRef.current.row !== -1 || hoverRef.current.col !== -1) {
            hoverRef.current = { row: -1, col: -1 };
            cb.onCellLeave();
          }
        }
      }
    },
    [cellFromPixel, gridPosFromPixel, hitTest],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      (e.target as Element).releasePointerCapture(e.pointerId);
      draggingRef.current = false;
      const p = propsRef.current;
      const cb = callbacksRef.current;

      if (p.mode === 'image') {
        overlayDragRef.current = null;
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.style.cursor =
            p.overlayInteractive && p.pendingOverlayImage && p.overlayVisible ? 'grab' : 'default';
        }
      } else if (p.mode === 'regions') {
        if (resizeDragRef.current) {
          const rd = resizeDragRef.current;
          const minR = Math.min(rd.fixedCell.row, rd.currentCell.row);
          const maxR = Math.max(rd.fixedCell.row, rd.currentCell.row);
          const minC = Math.min(rd.fixedCell.col, rd.currentCell.col);
          const maxC = Math.max(rd.fixedCell.col, rd.currentCell.col);
          cb.onRegionResize(rd.regionIndex, {
            row: minR, col: minC,
            width: maxC - minC, height: maxR - minR,
          });
          resizeDragRef.current = null;
        } else if (createDragRef.current) {
          const cd = createDragRef.current;
          const r1 = Math.min(cd.start.row, cd.end.row);
          const r2 = Math.max(cd.start.row, cd.end.row) + 1;
          const c1 = Math.min(cd.start.col, cd.end.col);
          const c2 = Math.max(cd.start.col, cd.end.col) + 1;
          cb.onRegionCreate({ row: r1, col: c1, width: c2 - c1, height: r2 - r1 });
          createDragRef.current = null;
        }
      } else {
        cb.onCellUp();
      }
    },
    [],
  );

  const handlePointerLeave = useCallback(() => {
    hoverRef.current = { row: -1, col: -1 };
    overlayDragRef.current = null;
    callbacksRef.current.onCellLeave();
  }, []);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      const p = propsRef.current;
      if (p.mode !== 'image' || !p.overlayInteractive || !p.pendingOverlayImage || !p.overlayVisible) return;
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.92 : 1.08;
      callbacksRef.current.onOverlayScaleChange(p.overlayScale * factor);
    },
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelListener = (event: WheelEvent) => {
      handleWheel(event);
    };

    canvas.addEventListener('wheel', wheelListener, { passive: false });
    return () => canvas.removeEventListener('wheel', wheelListener);
  }, [handleWheel]);

  /* --- Draw loop --- */

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = (time: number) => {
      const p = propsRef.current;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const { mrl, mcl } = getClueSlots(p);
      const tCols = mrl + p.cols;
      const tRows = mcl + p.rows;
      const cw = w / tCols;
      const ch = h / tRows;
      const cornerRadius = Math.min(cw, ch) * 0.14;
      const gameLeft = mrl * cw;
      const gameTop = mcl * ch;

      /* 1. Background */
      ctx.fillStyle = COL_BG;
      ctx.fillRect(0, 0, w, h);

      /* 2. Image overlay background(s) */
      if (p.overlayVisible) {
        for (const layer of p.overlayLayers) {
          drawOverlayImage(
            ctx,
            layer.image,
            layer.placement,
            gameLeft,
            gameTop,
            cw,
            ch,
            p.overlayOpacity,
          );
        }
        drawOverlayImage(
          ctx,
          p.pendingOverlayImage,
          p.pendingOverlayPlacement,
          gameLeft,
          gameTop,
          cw,
          ch,
          p.overlayOpacity,
        );
      }

      /* Crosshair (draw mode) */
      if (p.mode === 'draw') {
        if (p.previewMode === 'spray' && p.previewCells.size > 0) {
          const sprayRows = new Set<number>();
          const sprayCols = new Set<number>();
          for (const key of p.previewCells) {
            const [rowText, colText] = key.split(',');
            sprayRows.add(Number(rowText));
            sprayCols.add(Number(colText));
          }

          ctx.fillStyle = COL_CROSSHAIR;
          for (const row of sprayRows) {
            ctx.fillRect(0, (mcl + row) * ch, w, ch);
          }
          for (const col of sprayCols) {
            ctx.fillRect((mrl + col) * cw, 0, cw, h);
          }
        } else {
          const hr = hoverRef.current.row;
          const hc = hoverRef.current.col;
          if (hr >= 0 && hc >= 0) {
            ctx.fillStyle = COL_CROSSHAIR;
            ctx.fillRect(0, (mcl + hr) * ch, w, ch);
            ctx.fillRect((mrl + hc) * cw, 0, cw, h);
          }
        }
      }

      /* 3. Grid cells */
      for (let r = 0; r < p.rows; r++) {
        for (let c = 0; c < p.cols; c++) {
          if (p.grid[r]?.[c]) {
            const x = (mrl + c) * cw;
            const y = (mcl + r) * ch;
            const isTL = r === 0 && c === 0;
            const isBR = r === p.rows - 1 && c === p.cols - 1;
            ctx.fillStyle = COL_FILLED;
            if (isTL || isBR) {
              drawRoundedRect(ctx, x, y, cw, ch, isTL ? cornerRadius : 0);
              ctx.fill();
            } else {
              ctx.fillRect(x, y, cw, ch);
            }
          }
        }
      }

      /* 4. Pending import preview cells */
      if (p.pendingImportGrid) {
        ctx.fillStyle = COL_IMPORT_PREVIEW;
        for (let r = 0; r < p.rows; r++) {
          for (let c = 0; c < p.cols; c++) {
            if (!p.pendingImportGrid[r]?.[c]) continue;
            ctx.fillRect((mrl + c) * cw, (mcl + r) * ch, cw, ch);
          }
        }
      }

      /* 5. Uniqueness hints — single + or − on the next cell to toggle */
      const hintsToRender: ({ row: number; col: number; shouldFill: boolean })[] = [];
      if (p.mode === 'draw') {
        if (p.regions.length > 1) {
          // Per-region hints
          for (const hint of p.regionUniquenessHints ?? []) {
            if (hint) hintsToRender.push(hint);
          }
        } else if (p.uniquenessHint) {
          hintsToRender.push(p.uniquenessHint);
        }
      }
      if (hintsToRender.length > 0) {
        const pulse = 0.55 + Math.sin(((time / 1000) % 2) * Math.PI) * 0.35;
        const fontSize = Math.max(10, Math.min(cw, ch) * 0.7);
        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = COL_AMBIGUOUS;
        ctx.globalAlpha = pulse;
        for (const hint of hintsToRender) {
          ctx.fillText(hint.shouldFill ? '+' : '−', (mrl + hint.col + 0.5) * cw, (mcl + hint.row + 0.5) * ch);
        }
        ctx.globalAlpha = 1;
      }

      /* 6. Region boundaries (both modes) */
      if (p.regions.length > 0) {
        for (let i = 0; i < p.regions.length; i++) {
          const region = p.regions[i];
          const palette = getRegionPalette(i);
          const rx = gameLeft + region.col * cw;
          const ry = gameTop + region.row * ch;
          const rw = region.width * cw;
          const rh = region.height * ch;

          // Fill
          ctx.fillStyle = palette.fill;
          ctx.fillRect(rx, ry, rw, rh);

          // Border
          ctx.strokeStyle = palette.border;
          ctx.lineWidth = p.selectedRegion === i ? 3 : 1.5;
          ctx.strokeRect(rx + 0.5, ry + 0.5, rw - 1, rh - 1);
        }
      }

      /* 7. Tool previews (draw mode) */
      if (p.mode === 'draw' && p.previewCells.size > 0) {
        const color =
          p.previewMode === 'brush'
            ? COL_BRUSH_PREVIEW
            : p.previewMode === 'spray'
              ? COL_SPRAY_PREVIEW
              : COL_BRUSH_PREVIEW;
        ctx.fillStyle = color;
        for (const key of p.previewCells) {
          const [rs, cs] = key.split(',');
          ctx.fillRect((mrl + Number(cs)) * cw, (mcl + Number(rs)) * ch, cw, ch);
        }
      }

      /* Drag previews (multi puzzle mode) */
      if (p.mode === 'regions') {
        // Resize drag
        const rd = resizeDragRef.current;
        if (rd) {
          const minR = Math.min(rd.fixedCell.row, rd.currentCell.row);
          const maxR = Math.max(rd.fixedCell.row, rd.currentCell.row);
          const minC = Math.min(rd.fixedCell.col, rd.currentCell.col);
          const maxC = Math.max(rd.fixedCell.col, rd.currentCell.col);
          const rx = gameLeft + minC * cw;
          const ry = gameTop + minR * ch;
          const rw = (maxC - minC) * cw;
          const rh = (maxR - minR) * ch;
          ctx.strokeStyle = 'rgba(33, 150, 243, 0.8)';
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 4]);
          ctx.strokeRect(rx, ry, rw, rh);
          ctx.setLineDash([]);
          ctx.fillStyle = 'rgba(33, 150, 243, 0.08)';
          ctx.fillRect(rx, ry, rw, rh);
        }

        // Create drag — grey if too small, blue if valid
        const cd = createDragRef.current;
        if (cd) {
          const r1 = Math.min(cd.start.row, cd.end.row);
          const r2 = Math.max(cd.start.row, cd.end.row) + 1;
          const c1 = Math.min(cd.start.col, cd.end.col);
          const c2 = Math.max(cd.start.col, cd.end.col) + 1;
          const dragW = c2 - c1;
          const dragH = r2 - r1;
          const valid = dragW >= 3 && dragH >= 3;
          const rx = gameLeft + c1 * cw;
          const ry = gameTop + r1 * ch;
          const rw = dragW * cw;
          const rh = dragH * ch;
          ctx.strokeStyle = valid ? 'rgba(33, 150, 243, 0.7)' : 'rgba(158, 158, 158, 0.5)';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 4]);
          ctx.strokeRect(rx, ry, rw, rh);
          ctx.setLineDash([]);
          ctx.fillStyle = valid ? 'rgba(33, 150, 243, 0.1)' : 'rgba(158, 158, 158, 0.06)';
          ctx.fillRect(rx, ry, rw, rh);
        }
      }

      /* 8. Grid lines */
      const lineW = Math.max(1, cw * 0.024);
      const sepW = Math.max(1.5, cw * 0.06);

      ctx.fillStyle = COL_GRID;
      for (let ri = 1; ri < p.rows; ri++) {
        if (ri % 5 === 0) continue;
        ctx.fillRect(0, gameTop + ri * ch - lineW / 2, w, lineW);
      }
      for (let ci = 1; ci < p.cols; ci++) {
        if (ci % 5 === 0) continue;
        ctx.fillRect(gameLeft + ci * cw - lineW / 2, 0, lineW, h);
      }

      ctx.fillStyle = COL_SEP;
      for (let ri = 5; ri < p.rows; ri += 5) {
        ctx.fillRect(0, gameTop + ri * ch - sepW / 2, w, sepW);
      }
      for (let ci = 5; ci < p.cols; ci += 5) {
        ctx.fillRect(gameLeft + ci * cw - sepW / 2, 0, sepW, h);
      }

      // Border
      ctx.fillStyle = COL_GRID;
      ctx.fillRect(gameLeft + cornerRadius, gameTop - lineW / 2, w - gameLeft - cornerRadius, lineW);
      ctx.fillRect(gameLeft - lineW / 2, gameTop + cornerRadius, lineW, h - gameTop - cornerRadius);
      ctx.strokeStyle = COL_GRID;
      ctx.lineWidth = lineW;
      ctx.beginPath();
      ctx.arc(gameLeft + cornerRadius, gameTop + cornerRadius, cornerRadius, Math.PI, Math.PI * 1.5);
      ctx.stroke();
      ctx.fillStyle = COL_GRID;
      ctx.fillRect(w - lineW, gameTop, lineW, h - gameTop);
      ctx.fillRect(gameLeft, h - lineW, w - gameLeft, lineW);

      /* 9. Region controls on top of grid (multi puzzle mode) */
      if (p.mode === 'regions' && p.regions.length > 0) {
        const handleSize = Math.max(5, Math.min(9, Math.min(cw, ch) * 0.28));

        for (let i = 0; i < p.regions.length; i++) {
          const region = p.regions[i];
          const palette = getRegionPalette(i);
          const borderColor = palette.border;

          // Resize handles — colored triangles at corners
          const tri = handleSize * 3;
          ctx.fillStyle = borderColor;
          const tlx = gameLeft + region.col * cw;
          const tly = gameTop + region.row * ch;
          ctx.beginPath(); ctx.moveTo(tlx, tly); ctx.lineTo(tlx + tri, tly); ctx.lineTo(tlx, tly + tri); ctx.closePath(); ctx.fill();
          const trx = gameLeft + (region.col + region.width) * cw;
          const try_ = gameTop + region.row * ch;
          ctx.beginPath(); ctx.moveTo(trx, try_); ctx.lineTo(trx - tri, try_); ctx.lineTo(trx, try_ + tri); ctx.closePath(); ctx.fill();
          const blx = gameLeft + region.col * cw;
          const bly = gameTop + (region.row + region.height) * ch;
          ctx.beginPath(); ctx.moveTo(blx, bly); ctx.lineTo(blx + tri, bly); ctx.lineTo(blx, bly - tri); ctx.closePath(); ctx.fill();
          const brx = gameLeft + (region.col + region.width) * cw;
          const bry = gameTop + (region.row + region.height) * ch;
          ctx.beginPath(); ctx.moveTo(brx, bry); ctx.lineTo(brx - tri, bry); ctx.lineTo(brx, bry - tri); ctx.closePath(); ctx.fill();

          // "Remove" pill
          const isHovered = hoverDeleteRef.current === i;
          const remFontSize = Math.min(cw, ch) * 0.36;
          ctx.font = `700 ${remFontSize}px 'Lilita One', sans-serif`;
          const fitsTopRight = region.width >= 4;
          const remCx = fitsTopRight
            ? gameLeft + (region.col + region.width - 2) * cw
            : gameLeft + (region.col + region.width / 2) * cw;
          const remCy = fitsTopRight
            ? gameTop + (region.row + 0.5) * ch
            : gameTop + (region.row + region.height / 2) * ch;
          const pillW = cw * 1.9;
          const pillH = ch * 0.6;
          const pillR = pillH / 2;
          const pillX = remCx - pillW / 2;
          const pillY = remCy - pillH / 2;
          ctx.save();
          ctx.shadowColor = 'rgba(0,0,0,0.12)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetY = 1;
          ctx.fillStyle = isHovered ? 'rgba(198, 40, 40, 0.18)' : 'rgba(255,255,255,0.9)';
          ctx.beginPath();
          ctx.moveTo(pillX + pillR, pillY); ctx.lineTo(pillX + pillW - pillR, pillY);
          ctx.arc(pillX + pillW - pillR, pillY + pillR, pillR, -Math.PI / 2, Math.PI / 2);
          ctx.lineTo(pillX + pillR, pillY + pillH);
          ctx.arc(pillX + pillR, pillY + pillR, pillR, Math.PI / 2, -Math.PI / 2);
          ctx.closePath(); ctx.fill();
          ctx.restore();
          ctx.strokeStyle = isHovered ? '#c62828' : borderColor;
          ctx.lineWidth = isHovered ? 1.5 : 1;
          ctx.beginPath();
          ctx.moveTo(pillX + pillR, pillY); ctx.lineTo(pillX + pillW - pillR, pillY);
          ctx.arc(pillX + pillW - pillR, pillY + pillR, pillR, -Math.PI / 2, Math.PI / 2);
          ctx.lineTo(pillX + pillR, pillY + pillH);
          ctx.arc(pillX + pillR, pillY + pillR, pillR, Math.PI / 2, -Math.PI / 2);
          ctx.closePath(); ctx.stroke();
          ctx.fillStyle = isHovered ? '#c62828' : palette.clue;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('Remove', remCx, remCy);
        }
      }

      /* Region number + difficulty + uniqueness labels (both modes, on top of grid) */
      if (p.regions.length > 0) {
        const labelSize = Math.min(cw, ch) * 0.5;
        ctx.font = `800 ${labelSize}px 'Lilita One', sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const multi = p.regions.length > 1;
        for (let i = 0; i < p.regions.length; i++) {
          const region = p.regions[i];
          const difficulty = p.regionDifficulties?.[i];
          const unique = p.regionUniqueness?.[i];
          const palette = getRegionPalette(i);
          const parts: string[] = [];
          if (multi) parts.push(`${i + 1}`);
          if (difficulty != null && difficulty > 0) parts.push(`D${difficulty}`);
          const labelX = gameLeft + region.col * cw + 3;
          const labelY = gameTop + region.row * ch + 2;
          if (parts.length > 0) {
            ctx.fillStyle = palette.border;
            ctx.fillText(parts.join(' '), labelX, labelY);
          }
          // Uniqueness label below the number/difficulty
          if (unique === false) {
            const smallSize = labelSize * 0.7;
            ctx.save();
            ctx.font = `800 ${smallSize}px 'Lilita One', sans-serif`;
            ctx.fillStyle = '#c44';
            ctx.fillText('not unique', labelX, labelY + labelSize + 1);
            ctx.restore();
          }
        }
      }

      /* 10. Clue numbers */
      const fontSize = Math.min(cw, ch) * 0.48;
      ctx.font = `800 ${fontSize}px 'Lilita One', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (p.showClues) {
        if (p.mode === 'draw' || p.mode === 'image' || p.regions.length === 0) {
          // Full-grid clues
          ctx.fillStyle = COL_CLUE;
          for (let c = 0; c < p.cols; c++) {
            const clue = p.clues.cols[c] ?? [0];
            const offset = mcl - clue.length;
            for (let i = 0; i < clue.length; i++) {
              ctx.fillText(`${clue[i]}`, (mrl + c) * cw + cw / 2, (offset + i) * ch + ch / 2);
            }
          }
          for (let r = 0; r < p.rows; r++) {
            const clue = p.clues.rows[r] ?? [0];
            const offset = mrl - clue.length;
            for (let i = 0; i < clue.length; i++) {
              ctx.fillText(`${clue[i]}`, (offset + i) * cw + cw / 2, (mcl + r) * ch + ch / 2);
            }
          }
        } else {
          // Per-region clues (sub-puzzles mode)
          for (let ri = 0; ri < p.regions.length; ri++) {
            const region = p.regions[ri];
            const rClues = p.regionClues[ri];
            if (!rClues) continue;
            ctx.fillStyle = getRegionPalette(ri).clue;

            // Row clues — left margin, at region's grid rows
            for (let r = 0; r < region.height; r++) {
              const clue = rClues.rows[r] ?? [0];
              const gridRow = region.row + r;
              const offset = mrl - clue.length;
              for (let i = 0; i < clue.length; i++) {
                ctx.fillText(`${clue[i]}`, (offset + i) * cw + cw / 2, (mcl + gridRow) * ch + ch / 2);
              }
            }

            // Column clues — top margin, at region's grid columns
            for (let c = 0; c < region.width; c++) {
              const clue = rClues.cols[c] ?? [0];
              const gridCol = region.col + c;
              const offset = mcl - clue.length;
              for (let i = 0; i < clue.length; i++) {
                ctx.fillText(`${clue[i]}`, (mrl + gridCol) * cw + cw / 2, (offset + i) * ch + ch / 2);
              }
            }
          }
        }
      }

      /* 11. Span hints (first row/col slot) */
      if (p.showSpanHints) {
        // Separators between span slot and the rest of the clue area
        ctx.fillStyle = COL_GRID;
        ctx.fillRect(cw - lineW / 2, gameTop, lineW, h - gameTop);
        ctx.fillRect(gameLeft, ch - lineW / 2, w - gameLeft, lineW);

        ctx.font = `800 ${fontSize}px 'Lilita One', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#b89060';

        // Row span hints (first column slot)
        for (let r = 0; r < p.rows; r++) {
          const clue = p.clues.rows[r] ?? [];
          const filled = clue.reduce((a, b) => a + b, 0);
          if (filled === 0) continue;
          const span = filled + Math.max(0, clue.length - 1);
          ctx.fillText(`${span}`, cw / 2, (mcl + r) * ch + ch / 2);
        }

        // Column span hints (first row slot)
        for (let c = 0; c < p.cols; c++) {
          const clue = p.clues.cols[c] ?? [];
          const filled = clue.reduce((a, b) => a + b, 0);
          if (filled === 0) continue;
          const span = filled + Math.max(0, clue.length - 1);
          ctx.fillText(`${span}`, (mrl + c) * cw + cw / 2, ch / 2);
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div
      style={{
        width: '100%',
        aspectRatio: `${totalCols} / ${totalRows}`,
        border: '2px solid var(--board-border)',
        borderRadius: 8,
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        cursor:
          mode === 'draw'
            ? 'crosshair'
            : mode === 'image' && overlayInteractive && pendingOverlayImage && overlayVisible
              ? 'grab'
              : 'default',
        flexShrink: 0,
      }}
      onPointerLeave={handlePointerLeave}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
}
